import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Compass, GlobeHemisphereEast, ClockCounterClockwise, CaretLeft } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { Seo } from "@/components/Seo";
import { Hero } from "@/components/Hero";
import { FilterBar } from "@/components/FilterBar";
import { SpotlightBanner } from "@/components/SpotlightBanner";
import { CountryExplorer } from "@/components/CountryExplorer";
import { ShowCard } from "@/components/ShowCard";
import { OTPModal } from "@/components/OTPModal";
import { ShowDetailModal } from "@/components/ShowDetailModal";
import { PremiumModal } from "@/components/PremiumModal";
import { Skeleton } from "@/components/ui/skeleton";
import { ConnectionStatus } from "@/components/ConnectionStatus";
import { FaqSection } from "@/components/FaqSection";
import { ExpoLinks } from "@/components/ExpoLinks";
import { LeadMagnet, getCalendar } from "@/components/LeadMagnet";
import { Helmet } from "react-helmet-async";
import { readShowsCache, writeShowsCache, clearShowsCache } from "@/lib/showsCache";

const RETRY_DELAYS = [1500, 3000, 6000];
import {
  fetchMeta,
  fetchShows,
  fetchFeatured,
  fetchMe,
  getToken,
  setToken,
  setEmail,
  getEmail,
  clearToken,
} from "@/lib/api";

export default function Directory({ archived = false }) {
  const [meta, setMeta] = useState({ countries: [], categories: [] });
  const [shows, setShows] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", country: "all", category: "all" });
  const [coords, setCoords] = useState(null);
  const [nearbyActive, setNearbyActive] = useState(false);

  const [email, setEmailState] = useState(getEmail() || "");
  const [premium, setPremium] = useState(false);
  const [verified, setVerified] = useState(!!getToken());

  const [otpOpen, setOtpOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [detailShow, setDetailShow] = useState(null);
  const [pendingShow, setPendingShow] = useState(null);
  const [pendingUpgrade, setPendingUpgrade] = useState(false);
  const [pendingAffiliate, setPendingAffiliate] = useState(null);
  const [otpReason, setOtpReason] = useState("details");
  const [pendingCalendar, setPendingCalendar] = useState(false);
  const [conn, setConn] = useState("ok");
  const [cachedAt, setCachedAt] = useState(null);
  const reqRef = useRef(0);

  const loadShows = useCallback(async () => {
    const reqId = ++reqRef.current;
    const params = {
      search: filters.search || undefined,
      country: filters.country,
      category: filters.category,
    };
    if (archived) params.archived = true;
    if (nearbyActive && coords) {
      params.lat = coords.lat;
      params.lng = coords.lng;
    }
    const cached = readShowsCache(params);
    if (cached) {
      setShows(cached.shows);
      setPremium(cached.premium);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setCachedAt(cached ? cached.ts : null);
    setConn("ok");
    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
      if (attempt > 0) {
        setConn("reconnecting");
        setLoading(false);
        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt - 1]));
      }
      if (reqId !== reqRef.current) return;
      try {
        const data = await fetchShows(params);
        if (reqId !== reqRef.current) return;
        writeShowsCache(params, data);
        setShows(data.shows);
        setPremium(data.premium);
        setCachedAt(null);
        setConn("ok");
        setLoading(false);
        return;
      } catch (e) {
        console.warn(`Shows request failed (attempt ${attempt + 1})`, e);
      }
    }
    if (reqId !== reqRef.current) return;
    setConn("offline");
    setLoading(false);
  }, [filters, nearbyActive, coords, archived]);

  useEffect(() => {
    fetchMeta().then(setMeta).catch(() => {});
  }, []);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const c = searchParams.get("country");
    const cat = searchParams.get("category");
    if (c || cat) {
      setFilters((f) => ({ ...f, country: c || f.country, category: cat || f.category }));
    }
    if (searchParams.get("get") === "calendar") {
      setTimeout(() => document.querySelector('[data-testid="lead-magnet"]')?.scrollIntoView({ behavior: "smooth" }), 600);
      if (!getToken()) requestCalendar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFeatured = useCallback(() => {
    if (archived) return;
    fetchFeatured()
      .then((d) => setFeatured(d.featured))
      .catch(() => {});
  }, [archived]);

  useEffect(() => {
    loadFeatured();
  }, [loadFeatured, verified, premium]);

  useEffect(() => {
    const t = setTimeout(loadShows, 250);
    return () => clearTimeout(t);
  }, [loadShows]);

  useEffect(() => {
    if (getToken()) {
      fetchMe()
        .then((m) => {
          setVerified(true);
          setPremium(m.premium);
          setEmailState(m.email);
        })
        .catch(() => {
          clearToken();
          setVerified(false);
        });
    }
  }, []);

  const handleVerified = (res) => {
    setToken(res.token);
    setEmail(res.email);
    setEmailState(res.email);
    setVerified(true);
    setPremium(res.premium);
    clearShowsCache();
    loadShows();
    if (pendingShow) {
      const s = pendingShow;
      setPendingShow(null);
      setTimeout(() => setDetailShow(s), 300);
    }
    if (pendingUpgrade) {
      setPendingUpgrade(false);
      setTimeout(() => setPremiumOpen(true), 300);
    }
    if (pendingAffiliate) {
      const u = pendingAffiliate;
      setPendingAffiliate(null);
      setTimeout(() => window.open(u, "_blank", "noopener,noreferrer"), 300);
    }
    if (pendingCalendar) {
      setPendingCalendar(false);
      getCalendar().catch(() => toast.error("Couldn't prepare the calendar. Please try again."));
    }
  };

  const requestCalendar = () => {
    setPendingCalendar(true);
    setOtpReason("calendar");
    setOtpOpen(true);
  };

  const handleSignOut = () => {
    clearToken();
    localStorage.removeItem("wff_email");
    setVerified(false);
    setPremium(false);
    setEmailState("");
    clearShowsCache();
    loadShows();
    toast.success("Signed out");
  };

  const openDetail = (show) => {
    if (!verified) {
      setPendingShow(show);
      setOtpReason("details");
      setOtpOpen(true);
      toast.info("Verify your email to view full show details");
      return;
    }
    setDetailShow(show);
  };

  const handleNearby = () => {
    if (nearbyActive) {
      setNearbyActive(false);
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    toast.loading("Detecting your location…", { id: "geo" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setNearbyActive(true);
        toast.success("Showing shows nearest to you", { id: "geo" });
      },
      () => {
        toast.error("Location access denied", { id: "geo" });
      }
    );
  };

  const handleUpgradeClick = () => {
    if (!verified) {
      setPendingUpgrade(true);
      setOtpReason("upgrade");
      setOtpOpen(true);
      return;
    }
    setPremiumOpen(true);
  };

  const openAffiliate = (url, reason = "tickets") => {
    if (!url) return;
    if (!verified) {
      setPendingAffiliate(url);
      setOtpReason(reason);
      setOtpOpen(true);
      toast.info("Verify your email to continue to the event");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleTickets = (show) => openAffiliate(show.tickets_url || show.website, "tickets");
  const handleInquire = (show) => openAffiliate(show.inquire_url || show.website, "inquire");
  const handleAdCta = () =>
    openAffiliate("https://www.franchiseindia.net/fro_expo_mumbai_2026.php?ref=worldfranchiseforum", "tickets");

  const navigate = useNavigate();

  const scrollToDirectory = () => {
    document.getElementById("directory")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0B0D17]">
      {archived ? (
        <Seo
          title="Previous Shows — Archive"
          description="Browse concluded franchise & licensing trade shows from around the world. The historical archive of the World Franchise Forum directory."
          path="/previous-shows"
        />
      ) : (
        <Seo path="/" />
      )}
      <Header
        email={verified ? email : ""}
        premium={premium}
        onVerify={() => {
          setOtpReason("verify");
          setOtpOpen(true);
        }}
        onSignOut={handleSignOut}
      />

      {archived ? (
        <section className="border-b border-white/10 bg-[#0d1020]">
          <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
            <button
              data-testid="archive-back-btn"
              onClick={() => navigate("/")}
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white"
            >
              <CaretLeft size={15} /> Back to live directory
            </button>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-slate-300">
              <ClockCounterClockwise size={14} weight="fill" className="text-blue-400" /> The Archive
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Previous Shows
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-400">
              A historical record of franchise &amp; licensing events that have concluded. Browse the depth of our
              directory — {meta.archived_total ?? shows.length} past show{(meta.archived_total ?? shows.length) === 1 ? "" : "s"} archived and counting.
            </p>
          </div>
        </section>
      ) : (
        <>
          <Hero total={shows.length} countries={(meta.countries || []).length} onNearby={handleNearby} onExplore={scrollToDirectory} onAdCta={handleAdCta} />

          <SpotlightBanner
            show={featured}
            onOpen={openDetail}
            onUpgrade={handleUpgradeClick}
          />
          <LeadMagnet verified={verified} onNeedVerify={requestCalendar} total={meta.total} />
        </>
      )}

      <FilterBar
        meta={meta}
        filters={filters}
        setFilters={setFilters}
        nearbyActive={nearbyActive}
        onToggleNearby={handleNearby}
        resultCount={shows.length}
      />

      {!archived && (
        <CountryExplorer
          countryCounts={meta.country_counts}
          total={meta.total}
          active={filters.country}
          onSelect={(c) => {
            setFilters((f) => ({ ...f, country: c }));
            setTimeout(() => document.getElementById("directory")?.scrollIntoView({ behavior: "smooth" }), 100);
          }}
        />
      )}

      <main id="directory" className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <ConnectionStatus status={conn} cachedAt={cachedAt} onRetry={loadShows} />
        {loading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[420px] rounded-2xl bg-white/[0.04]" />
            ))}
          </div>
        ) : shows.length === 0 && conn === "reconnecting" ? (
          <div className="grid grid-cols-1 gap-8 opacity-60 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[420px] rounded-2xl bg-white/[0.04]" />
            ))}
          </div>
        ) : shows.length === 0 && conn === "offline" ? (
          <div data-testid="shows-offline-state" className="flex flex-col items-center justify-center gap-4 py-24 text-center">
            <GlobeHemisphereEast size={48} weight="duotone" className="text-slate-600" />
            <p className="font-display text-xl font-bold text-white">We can't reach the directory right now</p>
            <p className="text-sm text-slate-400">Your connection or our server is having a moment. Use Retry above.</p>
          </div>
        ) : shows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
            <GlobeHemisphereEast size={48} weight="duotone" className="text-slate-600" />
            <p className="font-display text-xl font-bold text-white">
              {archived ? "No past shows yet" : "No shows match your filters"}
            </p>
            <p className="text-sm text-slate-400">
              {archived ? "As events conclude they'll appear here automatically." : "Try clearing filters or search a different market."}
            </p>
          </div>
        ) : (
          <div
            data-testid="shows-grid"
            className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
          >
            {shows.map((show, i) => (
              <ShowCard
                key={show.id}
                show={show}
                index={i}
                archived={archived}
                onOpen={openDetail}
                onTickets={handleTickets}
                onUpgrade={() => {
                  if (!verified) {
                    setPendingUpgrade(true);
                    setOtpReason("upgrade");
                    setOtpOpen(true);
                    return;
                  }
                  setPremiumOpen(true);
                }}
              />
            ))}
          </div>
        )}
      </main>

      {!archived && <FaqSection country={filters.country} />}
      {!archived && shows.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: "Upcoming franchise expos & shows",
              itemListElement: shows.slice(0, 30).map((s, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "Event",
                  name: s.name,
                  startDate: s.start_date,
                  endDate: s.end_date || s.start_date,
                  eventStatus: "https://schema.org/EventScheduled",
                  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
                  image: [s.image_url],
                  description: s.description,
                  url: `${(process.env.REACT_APP_PUBLIC_SITE_URL || window.location.origin).replace(/\/$/, "")}/show/${s.id}`,
                  location: {
                    "@type": "Place",
                    name: s.venue || s.city,
                    address: { "@type": "PostalAddress", addressLocality: s.city, addressCountry: s.country },
                  },
                },
              })),
            })}
          </script>
        </Helmet>
      )}

      <footer className="border-t border-white/10 py-10">
        {!archived && <ExpoLinks />}
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-5 text-center sm:px-8">
          <div className="flex items-center gap-2 text-slate-400">
            <Compass size={18} weight="duotone" className="text-blue-400" />
            <span className="font-display text-sm font-semibold text-white">World Franchise Forum</span>
          </div>
          <nav className="flex items-center gap-5 text-sm">
            <Link
              to="/"
              data-testid="footer-directory-link"
              className="text-slate-400 transition-colors hover:text-white"
            >
              Live Directory
            </Link>
            <span className="h-3 w-px bg-white/15" />
            <Link
              to="/previous-shows"
              data-testid="footer-previous-shows-link"
              className="inline-flex items-center gap-1.5 text-slate-400 transition-colors hover:text-white"
            >
              <ClockCounterClockwise size={14} weight="fill" className="text-blue-400" /> Previous Shows
            </Link>
          </nav>
          <p className="text-xs text-slate-500">
            AI-refreshed weekly · Covering US · UK · UAE · Malaysia · Indonesia · India
          </p>
        </div>
      </footer>

      <OTPModal open={otpOpen} onOpenChange={setOtpOpen} onVerified={handleVerified} reason={otpReason} />
      <ShowDetailModal
        show={detailShow}
        open={!!detailShow}
        onOpenChange={(o) => !o && setDetailShow(null)}
        onInquire={handleInquire}
        onTickets={handleTickets}
        onUpgrade={() => {
          setDetailShow(null);
          setTimeout(() => setPremiumOpen(true), 250);
        }}
      />
      <PremiumModal
        open={premiumOpen}
        onOpenChange={setPremiumOpen}
        verified={verified}
        onNeedVerify={() => {
          setPendingUpgrade(true);
          setOtpReason("upgrade");
          setOtpOpen(true);
        }}
      />
    </div>
  );
}
